import * as Service from 'types/service';
import { Button } from 'bento';
import {
  EventAction,
  EventAttributesActionLocation,
} from 'utils/analytics/amplitudeEventConstants';
import { IconArrowDownCircle, IconCheckmarkCircle } from '@tocktix/pantry';
import { Theme, css } from '@emotion/react';
import { ViewMode } from 'routes/authenticated/app/bookings/CalendarAndServiceRoute';
import { canMarkPartyAsLeft, getButtonActions, onFinishPartyCheck } from 'utils/partyUtils';
import { includes, isEmpty } from 'es-toolkit/compat';
import { secondaryStatesSelector } from 'selectors/service/secondaryStatesSelector';
import { serivceConfigBusinessSeatedStatusSelector } from 'selectors/service/serviceConfigSelector';
import { servicePartyDragModeSelector } from 'selectors/service/serviceUiSelector';
import { trackPartyEventUtility } from 'components/service/party/PartyPopoverFC';
import { useActions } from '@tock/shared/utils/connect';
import { useSelector } from 'react-redux';
import IconMove from 'components/icons/svgr/IconMove';
import PartyMoveButton from 'components/service/party/PartyMoveButton';
import PartyStates from 'constants/partyStateConstants';

interface Props {
  isReadOnly: boolean;
  party: Service.Party;
  viewMode: ViewMode;
  onClose(): void;
  onFinishSeatedPartiesAtTablesAndSeatNewParty(tableIds: number[], party: Service.Party): void;
  handleSelectParty(party: Service.Party): void;
}

const PartyPopoverActionButton: React.FC<React.PropsWithChildren<Props>> = ({
  isReadOnly,
  party,
  viewMode,
  onClose,
  onFinishSeatedPartiesAtTablesAndSeatNewParty,
  handleSelectParty,
}) => {
  const dragMode = useSelector(servicePartyDragModeSelector);
  const secondaryStates = useSelector(secondaryStatesSelector);
  const seatedStatusSelector = useSelector(serivceConfigBusinessSeatedStatusSelector);
  const { actions } = useActions();
  const hasSupportForMovingParties = viewMode.mode === 'service';

  const onUpdatePartyState = (
    party: Service.Party,
    state: string,
    secondaryState: string | null
  ): void => {
    actions.elmParties.updatePartyState(
      party,
      state,
      secondaryState,
      [],
      EventAttributesActionLocation.PARTY_POPOVER
    );
    onClose();
  };

  const trackPartyEvent = (action: EventAction) => {
    return trackPartyEventUtility(action, party);
  };

  const seatParty = (party: Service.Party): void => {
    onFinishSeatedPartiesAtTablesAndSeatNewParty(party.tableIds, party);
    onClose();
  };

  const finishParty = (party: Service.Party): void => {
    if (party.state === PartyStates.left) {
      return;
    }
    if (!canMarkPartyAsLeft(party)) {
      onFinishPartyCheck(party, onUpdatePartyState);
      return;
    }
    onUpdatePartyState(party, PartyStates.left, null);
    onClose();
  };

  const assignTableAndToggleGuest = () => {
    actions.serviceUiPartyDisplay.toggleIsAssigningTables();
    handleSelectParty(party);
    onClose();

    trackPartyEvent(EventAction.PARTY_TABLE_ASSIGNMENT_STARTED);
  };

  const { buttonText, buttonAction, isDisabled } = getButtonActions(
    party,
    secondaryStates,
    seatParty,
    finishParty
  );

  const buttonColor =
    buttonText === 'Seat'
      ? `var(--spec-${seatedStatusSelector.color})!important`
      : buttonText === 'Finish'
        ? 'var(--spec-lavender)!important'
        : '';

  const buttonIcon =
    buttonText === 'Seat' ? (
      <IconArrowDownCircle />
    ) : buttonText === 'Finish' ? (
      <IconCheckmarkCircle />
    ) : (
      <></>
    );

  const walkInButton = () => {
    return (
      <>
        {!isEmpty(party.tableIds) && buttonText && (
          <Button
            startIcon={buttonIcon}
            disabled={isReadOnly}
            css={css`
              background-color: ${buttonColor};
              border-color: ${buttonColor};
              flex-grow: 1;
            `}
            size="large"
            onClick={buttonAction}
          >
            {buttonText}
          </Button>
        )}
      </>
    );
  };

  return (
    <div
      data-testid="popover-action-button-container"
      css={css`
        display: flex;
        width: 100%;
        gap: 1rem;
      `}
    >
      {hasSupportForMovingParties && !includes(['CANCELED', 'LEFT', 'NO_SHOW'], party.state) && (
        <PartyMoveButton
          IconComponent={IconMove}
          disabled={isReadOnly}
          dragMode={dragMode}
          party={party}
          isReadOnly={isReadOnly}
          dragSource="PARTY_MENU"
          stretch
          variant="primary"
          onClick={assignTableAndToggleGuest}
          onSuccessfulDrop={onClose}
        />
      )}
      {party.type === 'WalkIn'
        ? walkInButton()
        : buttonText &&
          party.tableIds.length > 0 && (
            <Button
              aria-label="Party Action"
              startIcon={buttonIcon}
              disabled={isReadOnly || isDisabled}
              css={(theme: Theme) => css`
                background-color: ${isReadOnly ? theme.colors.bgLight : buttonColor};
                border-color: ${isReadOnly ? theme.colors.fgDisabled : buttonColor};
                flex: 1 1 auto;
              `}
              size="large"
              onClick={buttonAction}
              variant="contained"
            >
              {buttonText}
            </Button>
          )}
    </div>
  );
};

export default PartyPopoverActionButton;
